import { Plus } from 'lucide-react';
import { differenceInCalendarDays, isThisWeek, isToday, isTomorrow, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { CompanionCard, usePager } from '@/components/companion/CompanionCard';
import { formatTime, formatWeekday } from '@/i18n/intl';
import { msg, useMsg, type Msg } from '@/i18n/msg';
import { isScheduledAppointment } from '@/lib/parent-home-noticing';
import type { MedicalAppointment, Task } from '@/types/api';

const MEDICAL_TYPES_NEEDING_RIDES = new Set([
  'chemotherapy',
  'radiation',
  'scan',
  'lab',
  'oncology_consult',
  'surgery',
]);

/**
 * The note, its buttons and what they send are message descriptors
 * (#2109 R3-B3): the card resolves them at render, and the page resolves
 * the prompt and the prefilled title when a button is pressed, so both
 * follow a language switch. The prompt is written in the UI language
 * (R3-B2, ruled 2026-10-15).
 */
export interface CalloutAction {
  id: string;
  label: Msg;
  primary?: boolean;
  onClick: () => void;
}

export interface CalloutSuggestion {
  id: string;
  body: Msg;
  actions: CalloutAction[];
}

interface BuildArgs {
  tasks: Task[];
  appointments: MedicalAppointment[];
  onDraftRideRequest: (defaultDate: Date, suggestedTitle: Msg) => void;
  onTalkToStevens: (prompt: Msg) => void;
  onFilterUrgent: () => void;
  companionName: string;
  /** The formatting locale for the time and weekday params; the page passes the UI language and rebuilds on a switch. */
  locale?: string;
}

export function buildSuggestions({
  tasks,
  appointments,
  onDraftRideRequest,
  onTalkToStevens,
  onFilterUrgent,
  companionName,
  locale,
}: BuildArgs): CalloutSuggestion[] {
  const out: CalloutSuggestion[] = [];
  // #2433: only a visit that is still on is waiting. A cancelled, attended
  // or missed one neither asks for a ride nor counts toward "appointments
  // coming up"; a legacy row with no status counts as scheduled.
  const live = appointments.filter(isScheduledAppointment);

  const upcomingMedical = live
    .map((a) => ({ a, dt: parseISO(a.date_time) }))
    .filter(({ a, dt }) => {
      const days = differenceInCalendarDays(dt, new Date());
      return days >= 0 && days <= 3 && MEDICAL_TYPES_NEEDING_RIDES.has(a.appointment_type);
    })
    .sort((a, b) => a.dt.getTime() - b.dt.getTime());

  const hasRideThisWeek = tasks.some((t) => {
    if (t.category !== 'ride') return false;
    const ref = t.date_time ?? t.created_at;
    if (!ref) return false;
    return isThisWeek(parseISO(ref), { weekStartsOn: 1 });
  });

  if (upcomingMedical.length > 0 && !hasRideThisWeek) {
    const next = upcomingMedical[0];
    const time = formatTime(next.dt, { locale });
    // One whole sentence per day shape, so no language inherits the English
    // "is today at" / "is Tuesday at" assembly.
    const body = isToday(next.dt)
      ? msg('tasks:callout.noRide.today', { title: next.a.title, time })
      : isTomorrow(next.dt)
        ? msg('tasks:callout.noRide.tomorrow', { title: next.a.title, time })
        : msg('tasks:callout.noRide.weekday', { title: next.a.title, weekday: formatWeekday(next.dt, { width: 'long', locale }), time });
    out.push({
      id: 'no-ride-near-medical',
      body,
      actions: [
        {
          id: 'draft-ride',
          label: msg('tasks:callout.draftRide'),
          primary: true,
          // The prefill is a starting point; the parent can still edit the saved title.
          onClick: () => onDraftRideRequest(next.dt, msg('tasks:callout.rideTitle', { title: next.a.title })),
        },
      ],
    });
  }

  const urgentUnclaimed = tasks.filter(
    (t) => t.status === 'open' && (t.urgency === 'critical' || t.urgency === 'high'),
  );
  if (urgentUnclaimed.length > 0) {
    const count = urgentUnclaimed.length;
    out.push({
      id: 'urgent-unclaimed',
      body: msg('tasks:callout.urgentOpen', { count }),
      actions: [
        {
          id: 'view-urgent',
          label: msg('tasks:callout.viewUrgent'),
          primary: true,
          onClick: onFilterUrgent,
        },
        {
          id: 'draft-nudge',
          label: msg('tasks:callout.draftNudge'),
          onClick: () => onTalkToStevens(msg('tasks:callout.nudgePrompt', { count })),
        },
      ],
    });
  }

  const recentTasks = tasks.filter((t) =>
    t.created_at ? differenceInCalendarDays(new Date(), parseISO(t.created_at)) <= 14 : false,
  );
  const upcomingApptCount = live.filter((a) => {
    const d = differenceInCalendarDays(parseISO(a.date_time), new Date());
    return d >= 0 && d <= 14;
  }).length;
  if (recentTasks.length === 0 && upcomingApptCount > 0) {
    out.push({
      id: 'quiet-list',
      body: msg('tasks:callout.quiet'),
      actions: [
        {
          id: 'talk',
          label: msg('tasks:callout.talkTo', { companion: companionName }),
          primary: true,
          onClick: () => onTalkToStevens(msg('tasks:callout.quietPrompt')),
        },
      ],
    });
  }

  return out;
}

interface StevensCalloutProps {
  suggestions: CalloutSuggestion[];
  /** `card` stacks for the right column (PageWithAside) — the only consumer left. */
  layout?: 'inline' | 'card';
}

/**
 * The companion's "noticed something" note on /tasks, rendered through the
 * shared CompanionCard (declutter 2026-09-10, spec §4 C5): this component
 * owns the patterns and the actions, the shell owns the shape and the
 * sentence-case label.
 */
export function StevensCallout({ suggestions, layout = 'card' }: StevensCalloutProps) {
  const resolve = useMsg();
  const { index, pager } = usePager(suggestions.length);
  const current = suggestions.length ? suggestions[index] : null;
  if (!current) return null;

  return (
    <CompanionCard layout={layout} body={resolve(current.body)} pager={pager} data-testid="tasks-companion-callout">
      {current.actions.map((a) => (
        <Button
          key={a.id}
          size="sm"
          variant={a.primary ? 'default' : 'ghost'}
          onClick={a.onClick}
          className={a.primary ? 'h-8 text-caption' : 'h-8 text-caption text-stevens-ink-3'}
        >
          {a.primary && <Plus className="h-3 w-3 mr-1" />}
          {resolve(a.label)}
        </Button>
      ))}
    </CompanionCard>
  );
}
